import { LinearProgress, Stack } from "@mui/material";
import { take } from "lodash";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../misc/useLimit";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import type { AppContext } from "./AppContext";
import OrdersActions from "./OrdersActions";
import { OrderCard } from "./orderCard/OrderCard";
import OrdersSearch from "./topBar/OrdersSearch";
import type { OrdersSearchCriteria } from "./topBar/useOrderSearch";
import { useFilteredOrders } from "./useFilteredOrders";
import { useOrdersSearchFromQueryParams } from "./useOrdersSearchFromQueryParams";

interface Props {
  readonly search: OrdersSearchCriteria;
  readonly onSearchChange: (value: Partial<OrdersSearchCriteria>) => void;
  readonly onSearchClear: () => void;
}

const OrdersPage: FC<Props> = ({ search, onSearchChange, onSearchClear }) => {
  const navigate = useNavigate();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore, resetLimit } = useLimit();
  const [appContext, setAppContext] = useState<AppContext>({
    showHistorical: false,
    selectedOrders: [],
  });
  const { context } = useAuthContext();
  const { orders, displayModeCounts, isLoading } = useFilteredOrders(search, appContext.showHistorical);

  const handleFiltersChange = (value: Partial<OrdersSearchCriteria>) => {
    onSearchChange(value);
    resetLimit();
  };

  useOrdersSearchFromQueryParams({
    fallbackAgentId: search.agentId,
    onApply: handleFiltersChange,
  });

  const role = context?.role;

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      <OrdersSearch
        search={search}
        displayModeCounts={displayModeCounts}
        context={context ?? undefined}
        showHistorical={appContext.showHistorical}
        onChange={(v) => handleFiltersChange(v)}
        onShowHistoricalChange={(showHistorical) => setAppContext((ctx) => ({ ...ctx, showHistorical }))}
        onClear={onSearchClear}
      />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < orders.length ? limit : orders.length}
        hasMore={orders.length > limit}
        fetchData={loadMore}
      >
        {take(orders, limit).map((o) => (
          <OrderCard
            key={o.id}
            role={role}
            value={o}
            showHistorical={appContext.showHistorical}
            onClick={() => navigate(o.id)}
            onContextMenu={() => openChildInNewTab(o.id)}
          />
        ))}
      </InfiniteScroll>
      {context && <OrdersActions userContext={context} onAction={() => navigate("new")} />}
    </Stack>
  );
};

export default OrdersPage;
